import React, { ReactNode } from 'react';
import { Box, List, ListItem, Pane, Divider, Text } from '@fullstory/components';
import { FormattedMessage } from 'react-intl';

const InlineCode = (children: ReactNode) => (
  <Pane
    data-component="InlineCode"
    inline
    backgroundColor="secondary"
    padding={[0, 4, 0, 4]}
    margin={[0, 4, 0, 4]}
    borderRadius={2}
    border="standard"
  >
    <Text appearance="mono3" color="brand">
      {children}
    </Text>
  </Pane>
);

export interface UrlTooltipProps {
  description: string;
  isCaseInsensitive?: boolean;
}

export const UrlTooltip: React.FC<UrlTooltipProps> = ({ description, isCaseInsensitive }) => (
  <Box data-component="UrlTooltip" padding={[8, 0, 8, 0]}>
    <Text appearance="bodySmall">{description}</Text>
    <Box margin={[16, 0, 12, 0]}>
      <Divider />
    </Box>
    <List disableGutters disablePadding>
      <ListItem>
        <Text appearance="bodySmall" tag="div">
          <FormattedMessage
            defaultMessage="Use <InlineCode>*</InlineCode> (wildcard) to match any single token at a position."
            id="NsGgEL"
            values={{ InlineCode }}
          />
        </Text>
      </ListItem>
      <ListItem>
        <Text appearance="bodySmall" tag="div">
          <FormattedMessage
            defaultMessage="Use <InlineCode>**</InlineCode> to match all paths that start with a common prefix."
            id="VZy0eM"
            values={{ InlineCode }}
          />
        </Text>
      </ListItem>
      <ListItem>
        <Text appearance="bodySmall" tag="div">
          <FormattedMessage
            defaultMessage="Use <InlineCode>[]</InlineCode> to match groups of tokens at particular positions."
            id="BGGVMu"
            values={{ InlineCode }}
          />
        </Text>
      </ListItem>
      {isCaseInsensitive ? (
        <ListItem>
          <Text appearance="bodySmall">
            <FormattedMessage defaultMessage="URLs are not case sensitive" id="k3AVZU" />
          </Text>
        </ListItem>
      ) : (
        <ListItem>
          <Text appearance="bodySmall">
            <FormattedMessage defaultMessage="URLs are case sensitive" id="kgeBOb" />
          </Text>
        </ListItem>
      )}
    </List>
    <Box margin={[12, 0, 16, 0]}>
      <Divider />
    </Box>
    <Box marginBottom={4}>
      <Text appearance="bodySmall">
        <FormattedMessage defaultMessage="Some examples:" id="XDFEMj" />
      </Text>
    </Box>
    <Pane inline backgroundColor="secondary" padding={8} borderRadius={2} border="standard">
      <Text appearance="mono3">
        <FormattedMessage defaultMessage="/product/" id="MR8sl+" />
        <Text appearance="mono3" color="brand" tag="span">
          *
        </Text>
        <FormattedMessage defaultMessage="/reviews/" id="3Cyd1O" />
        <Text appearance="mono3" color="brand" tag="span">
          *
        </Text>
      </Text>
      <Text appearance="mono3">
        <FormattedMessage defaultMessage="/search/category/shoes/" id="Tpy2mj" />
        <Text appearance="mono3" color="brand" tag="span">
          **
        </Text>
      </Text>
      <Text appearance="mono3">
        <FormattedMessage defaultMessage="/category/" id="bnvEMK" />
        <Text appearance="mono3" color="brand" tag="span">
          <FormattedMessage defaultMessage="[shoes, socks]" id="bu3Sib" />
        </Text>
      </Text>
    </Pane>
  </Box>
);
